/**
 * ConfidenceGauge - Compact horizontal gradient bar with position indicator,
 * or a simple colored badge in compact mode.
 *
 * Maps textual confidence levels (High, Medium-High, etc.) to a percentage
 * and renders either a red-amber-green gradient bar or an inline pill.
 */

import { cn } from '@/lib/utils';

const CONFIDENCE_MAP: Record<string, number> = {
  'High': 0.85,
  'Medium-High': 0.72,
  'Medium': 0.55,
  'Medium-Low': 0.40,
  'Low': 0.30,
};

const BADGE_COLORS: Record<string, string> = {
  'High': 'bg-green-500/20 text-green-400',
  'Medium-High': 'bg-emerald-500/20 text-emerald-400',
  'Medium': 'bg-amber-500/20 text-amber-400',
  'Medium-Low': 'bg-orange-500/20 text-orange-400',
  'Low': 'bg-red-500/20 text-red-400',
};

interface ConfidenceGaugeProps {
  level: string;
  className?: string;
  compact?: boolean;
}

export function ConfidenceGauge({ level, className, compact }: ConfidenceGaugeProps): JSX.Element {
  const percent = Math.round((CONFIDENCE_MAP[level] ?? 0.5) * 100);

  if (compact) {
    return (
      <span
        className={cn(
          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
          BADGE_COLORS[level] ?? 'bg-surface-light text-text-secondary',
          className,
        )}
      >
        {level}
      </span>
    );
  }

  return (
    <div className={cn('w-full max-w-[200px]', className)}>
      {/* Gradient bar with indicator */}
      <div className="relative h-3 rounded-full overflow-hidden bg-surface-light">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e)',
          }}
        />
        {/* Position indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 border-white bg-surface shadow-md z-10"
          style={{ left: `${percent}%` }}
        />
      </div>

      {/* Label */}
      <div className="mt-1.5 text-xs text-text-secondary text-center">
        {percent}% &mdash; {level}
      </div>
    </div>
  );
}
